import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  RouteReuseStrategy,
  provideRouter,
  withPreloading,
  PreloadAllModules,
} from '@angular/router';
import {
  IonicRouteStrategy,
  provideIonicAngular,
} from '@ionic/angular/standalone';

import { routes } from './app/app.routes';
import { AppComponent } from './app/app.component';
import { APP_INITIALIZER } from '@angular/core';
import { ThemeService } from 'src/app/core/services/theme.service';
import { pageTransitionAnimations } from './app/core/page-transitions/page-transitions';
import { provideFirebaseApp, initializeApp, getApp } from '@angular/fire/app';
import { provideAppCheck, initializeAppCheck, ReCaptchaEnterpriseProvider } from '@angular/fire/app-check';
import {
  provideAuth,
  getAuth,
  initializeAuth,
  browserLocalPersistence,
  connectAuthEmulator,
} from '@angular/fire/auth';
import { provideFirestore, getFirestore, connectFirestoreEmulator } from '@angular/fire/firestore';
import { provideStorage, getStorage, connectStorageEmulator } from '@angular/fire/storage';
import { environment } from './environments/environment';
import { Capacitor } from '@capacitor/core';
import { provideHighcharts } from 'highcharts-angular';
import { provideFunctions, getFunctions, connectFunctionsEmulator } from '@angular/fire/functions';

// Emulator host — 127.0.0.1 avoids iOS simulator IPv6 resolution issues that can hit with 'localhost'.
const EMULATOR_HOST = '127.0.0.1';
const EMULATOR_PORTS = {
  auth: 9099,
  firestore: 8080,
  functions: 5002,
  storage: 9199,
} as const;

// Enable App Check debug token on localhost so reCAPTCHA Enterprise doesn't block local dev.
// The SDK will print the debug token to the browser console on first load.
// Register that token in Firebase Console → App Check → Apps → Web app → Manage debug tokens.
if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
  (window as Window & { FIREBASE_APPCHECK_DEBUG_TOKEN?: boolean | string }).FIREBASE_APPCHECK_DEBUG_TOKEN = true;
}

bootstrapApplication(AppComponent, {
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: (themeService: ThemeService) => () => themeService.initializeFromLocalStorage(),
      deps: [ThemeService],
      multi: true,
    },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideAnimations(),
    provideIonicAngular({
      mode: 'ios',
      navAnimation: pageTransitionAnimations
    }),
    provideRouter(routes, withPreloading(PreloadAllModules)),
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => {
      const auth = Capacitor.isNativePlatform()
        ? initializeAuth(getApp(), { persistence: browserLocalPersistence })
        : getAuth();
      if (environment.emulator) {
        connectAuthEmulator(auth, `http://${EMULATOR_HOST}:${EMULATOR_PORTS.auth}`, {
          disableWarnings: true,
        });
      }
      return auth;
    }),
    provideFirestore(() => {
      const firestore = getFirestore();
      if (environment.emulator) {
        connectFirestoreEmulator(firestore, EMULATOR_HOST, EMULATOR_PORTS.firestore);
      }
      return firestore;
    }),
    provideFunctions(() => {
      const functions = getFunctions(getApp(), 'us-east4');
      if (environment.emulator) {
        connectFunctionsEmulator(functions, EMULATOR_HOST, EMULATOR_PORTS.functions);
      }
      return functions;
    }),
    provideStorage(() => {
      const storage = getStorage();
      if (environment.emulator) {
        connectStorageEmulator(storage, EMULATOR_HOST, EMULATOR_PORTS.storage);
      }
      return storage;
    }),
    // App Check - protects backend resources from abuse (production only via environment.beta.ts)
    // Uses reCAPTCHA Enterprise (key registered in Google Cloud Console → Security → reCAPTCHA Enterprise)
    // Only active when environment.appCheck is defined — intentionally absent from testing environments
    ...(environment.appCheck
      ? [
          provideAppCheck(() => {
            const provider = new ReCaptchaEnterpriseProvider(environment.appCheck!.recaptchaSiteKey);
            return initializeAppCheck(undefined, {
              provider,
              isTokenAutoRefreshEnabled: environment.appCheck!.isTokenAutoRefreshEnabled,
            });
          }),
        ]
      : []),
    provideHighcharts({
      // Global chart options applied across all charts
      options: {
        exporting: {
          enabled: false,
        },
        credits: {
          enabled: false,
        },
        title: {
          text: undefined,
        },
        legend: {
          enabled: false,
        },
        accessibility: {
          announceNewData: {
            enabled: true
          }
        },
        yAxis: {
          title: {
            text: undefined,
          },
        },
        xAxis: {
          title: {
            text: undefined,
          },
        }
      },

      // Include Highcharts additional modules (e.g., exporting, accessibility) or custom themes
      modules: () => {
        return [
          import('highcharts/esm/modules/accessibility'),
          // import('highcharts/esm/modules/exporting'),
          // import('highcharts/esm/themes/sunset'),
        ];
      },
    }),
  ],
});
